import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import { ChevronLeft, ChevronRight, Pause, Play, Square } from 'lucide-react'
import { photoAssetUrl } from '../lib/photos'

const EMPTY_COLLECTION = { type: 'FeatureCollection', features: [] }
const WEB_MERCATOR_LAT_LIMIT = 85.051129
const ISO2_TO_ISO3 = {"AD":"AND","AE":"ARE","AF":"AFG","AG":"ATG","AI":"AIA","AL":"ALB","AM":"ARM","AO":"AGO","AQ":"ATA","AR":"ARG","AS":"ASM","AT":"AUT","AU":"AUS","AW":"ABW","AX":"ALA","AZ":"AZE","BA":"BIH","BB":"BRB","BD":"BGD","BE":"BEL","BF":"BFA","BG":"BGR","BH":"BHR","BI":"BDI","BJ":"BEN","BL":"BLM","BM":"BMU","BN":"BRN","BO":"BOL","BQ":"BES","BR":"BRA","BS":"BHS","BT":"BTN","BV":"BVT","BW":"BWA","BY":"BLR","BZ":"BLZ","CA":"CAN","CC":"CCK","CD":"COD","CF":"CAF","CG":"COG","CH":"CHE","CI":"CIV","CK":"COK","CL":"CHL","CM":"CMR","CN":"CHN","CO":"COL","CR":"CRI","CU":"CUB","CV":"CPV","CW":"CUW","CX":"CXR","CY":"CYP","CZ":"CZE","DE":"DEU","DJ":"DJI","DK":"DNK","DM":"DMA","DO":"DOM","DZ":"DZA","EC":"ECU","EE":"EST","EG":"EGY","EH":"ESH","ER":"ERI","ES":"ESP","ET":"ETH","FI":"FIN","FJ":"FJI","FK":"FLK","FM":"FSM","FO":"FRO","FR":"FRA","GA":"GAB","GB":"GBR","GD":"GRD","GE":"GEO","GF":"GUF","GG":"GGY","GH":"GHA","GI":"GIB","GL":"GRL","GM":"GMB","GN":"GIN","GP":"GLP","GQ":"GNQ","GR":"GRC","GS":"SGS","GT":"GTM","GU":"GUM","GW":"GNB","GY":"GUY","HK":"HKG","HM":"HMD","HN":"HND","HR":"HRV","HT":"HTI","HU":"HUN","ID":"IDN","IE":"IRL","IL":"ISR","IM":"IMN","IN":"IND","IO":"IOT","IQ":"IRQ","IR":"IRN","IS":"ISL","IT":"ITA","JE":"JEY","JM":"JAM","JO":"JOR","JP":"JPN","KE":"KEN","KG":"KGZ","KH":"KHM","KI":"KIR","KM":"COM","KN":"KNA","KP":"PRK","KR":"KOR","KW":"KWT","KY":"CYM","KZ":"KAZ","LA":"LAO","LB":"LBN","LC":"LCA","LI":"LIE","LK":"LKA","LR":"LBR","LS":"LSO","LT":"LTU","LU":"LUX","LV":"LVA","LY":"LBY","MA":"MAR","MC":"MCO","MD":"MDA","ME":"MNE","MF":"MAF","MG":"MDG","MH":"MHL","MK":"MKD","ML":"MLI","MM":"MMR","MN":"MNG","MO":"MAC","MP":"MNP","MQ":"MTQ","MR":"MRT","MS":"MSR","MT":"MLT","MU":"MUS","MV":"MDV","MW":"MWI","MX":"MEX","MY":"MYS","MZ":"MOZ","NA":"NAM","NC":"NCL","NE":"NER","NF":"NFK","NG":"NGA","NI":"NIC","NL":"NLD","NO":"NOR","NP":"NPL","NR":"NRU","NU":"NIU","NZ":"NZL","OM":"OMN","PA":"PAN","PE":"PER","PF":"PYF","PG":"PNG","PH":"PHL","PK":"PAK","PL":"POL","PM":"SPM","PN":"PCN","PR":"PRI","PS":"PSE","PT":"PRT","PW":"PLW","PY":"PRY","QA":"QAT","RE":"REU","RO":"ROU","RS":"SRB","RU":"RUS","RW":"RWA","SA":"SAU","SB":"SLB","SC":"SYC","SD":"SDN","SE":"SWE","SG":"SGP","SH":"SHN","SI":"SVN","SJ":"SJM","SK":"SVK","SL":"SLE","SM":"SMR","SN":"SEN","SO":"SOM","SR":"SUR","SS":"SSD","ST":"STP","SV":"SLV","SX":"SXM","SY":"SYR","SZ":"SWZ","TC":"TCA","TD":"TCD","TF":"ATF","TG":"TGO","TH":"THA","TJ":"TJK","TK":"TKL","TL":"TLS","TM":"TKM","TN":"TUN","TO":"TON","TR":"TUR","TT":"TTO","TV":"TUV","TW":"TWN","TZ":"TZA","UA":"UKR","UG":"UGA","UM":"UMI","US":"USA","UY":"URY","UZ":"UZB","VA":"VAT","VC":"VCT","VE":"VEN","VG":"VGB","VI":"VIR","VN":"VNM","VU":"VUT","WF":"WLF","WS":"WSM","XK":"XKX","YE":"YEM","YT":"MYT","ZA":"ZAF","ZM":"ZMB","ZW":"ZWE"}
const STATUS_PRIORITY = { future: 0, planned: 1, completed: 2 }
const TOUR_ZOOM = {
  summit: 15,
  stop: 12,
}

// Both the dark "progress" map and the light map now share one progress
// treatment: countries are filled by how far along that country's summit is.
// Only the palette differs, so the light map reads as the same product rather
// than the stock multi-colour demo basemap.
const THEME_PALETTES = {
  progress: {
    background: '#0a0f1b',
    completed: '#3f9b68',
    planned: '#c7a24d',
    pending: '#252b3d',
    outline: '#4a5268',
    line: '#465068',
    lineOpacity: 0.58,
    text: '#bac3d2',
    textHalo: '#0a0f1b',
  },
  light: {
    background: '#eef1ec',
    completed: '#7fb894',
    planned: '#e0c072',
    pending: '#dfe3dc',
    outline: '#b4beb3',
    line: '#c0c9bf',
    lineOpacity: 0.7,
    text: '#33403a',
    textHalo: '#f7f8f4',
  },
}

// Blend two hex colours. Used to fade a planned country back toward the
// unvisited colour the further away its trip is.
function mixHex(fromHex, toHex, amount) {
  const parse = (value) => {
    const raw = String(value || '').trim().replace('#', '')
    const expanded = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw
    if (!/^[0-9a-f]{6}$/i.test(expanded)) return null
    return [0, 2, 4].map((index) => Number.parseInt(expanded.slice(index, index + 2), 16))
  }
  const from = parse(fromHex)
  const to = parse(toHex)
  if (!from || !to) return fromHex
  const t = Math.max(0, Math.min(1, amount))
  const mixed = from.map((channel, index) => Math.round(channel + (to[index] - channel) * t))
  return `#${mixed.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`
}

const MONTH_MS = 1000 * 60 * 60 * 24 * 30.44
const MAX_FADE = 0.55         // a year out is still a plan, so it stays clearly yellow
const FADE_HORIZON_MONTHS = 18 // ...and that is as faded as a dated trip gets
const UNDATED_FADE = 0.50      // no date yet: about the same, a touch yellower

// How far to fade a planned country: 0 = full colour (imminent),
// 1 = indistinguishable from unvisited.
function plannedFade(dateString, now) {
  if (!dateString) return UNDATED_FADE
  const date = new Date(`${dateString}T12:00:00`)
  if (Number.isNaN(date.getTime())) return UNDATED_FADE
  const months = (date.getTime() - now) / MONTH_MS
  if (months <= 0) return 0
  return Math.min(1, months / FADE_HORIZON_MONTHS) * MAX_FADE
}

function buildCountryProgressPaint(summits, palette, trips = [], now = Date.now()) {
  const statusByCountry = new Map()
  const dateByCountry = new Map()
  const tripStart = new Map(trips.map((trip) => [String(trip.id), trip.start_date]))

  summits.forEach((summit) => {
    const iso2 = String(summit.country_code || '').trim().toUpperCase()
    const iso3 = ISO2_TO_ISO3[iso2]
    if (!iso3) return

    const status = String(summit.status || 'future').toLowerCase()
    const previous = statusByCountry.get(iso3) || 'future'
    if ((STATUS_PRIORITY[status] || 0) >= (STATUS_PRIORITY[previous] || 0)) {
      statusByCountry.set(iso3, status)
    }

    // Earliest known date for the country decides how strongly it shows.
    const date = summit.summit_date || tripStart.get(String(summit.trip_id)) || ''
    if (date) {
      const existing = dateByCountry.get(iso3)
      if (!existing || String(date) < String(existing)) dateByCountry.set(iso3, date)
    }
  })

  const completed = []
  const plannedBranches = []
  statusByCountry.forEach((status, iso3) => {
    if (status === 'completed') {
      completed.push(iso3)
      return
    }
    if (status !== 'planned') return
    const fade = plannedFade(dateByCountry.get(iso3), now)
    plannedBranches.push([iso3, mixHex(palette.planned, palette.pending, fade)])
  })

  // One branch per planned country so each can carry its own faded colour.
  const expression = ['case', ['in', ['get', 'ADM0_A3'], ['literal', completed]], palette.completed]
  plannedBranches.forEach(([iso3, color]) => {
    expression.push(['==', ['get', 'ADM0_A3'], iso3], color)
  })
  expression.push(palette.pending)
  return expression
}

function styleProgressBasemap(map, countryPaint, palette) {
  const countryLayers = []
  const layers = map.getStyle()?.layers || []

  layers.forEach((layer) => {
    try {
      if (layer.type === 'background') {
        map.setPaintProperty(layer.id, 'background-color', palette.background)
        return
      }

      const sourceLayer = layer['source-layer']
      if (sourceLayer === 'countries' && layer.type === 'fill') {
        map.setPaintProperty(layer.id, 'fill-color', countryPaint)
        map.setPaintProperty(layer.id, 'fill-opacity', 0.94)
        map.setPaintProperty(layer.id, 'fill-outline-color', palette.outline)
        countryLayers.push(layer.id)
        return
      }

      // The MapLibre demo style ships Crimea as a separate GeoJSON fill layer,
      // so it does not inherit the country progress palette above. Neutralise it
      // in Progress mode rather than leaving the demo style's lavender fill.
      if (layer.id === 'crimea-fill' && layer.type === 'fill') {
        map.setPaintProperty(layer.id, 'fill-color', palette.pending)
        map.setPaintProperty(layer.id, 'fill-opacity', 0.94)
        map.setPaintProperty(layer.id, 'fill-outline-color', palette.outline)
        return
      }

      if (layer.type === 'line') {
        map.setPaintProperty(layer.id, 'line-color', palette.line)
        map.setPaintProperty(layer.id, 'line-opacity', palette.lineOpacity)
      }

      if (layer.type === 'symbol') {
        if (layer.layout?.['text-field']) {
          map.setPaintProperty(layer.id, 'text-color', palette.text)
          map.setPaintProperty(layer.id, 'text-halo-color', palette.textHalo)
          map.setPaintProperty(layer.id, 'text-halo-width', 1.25)
        }
      }
    } catch (_error) {
      // Some demo-style layers do not expose every paint property at every zoom.
    }
  })

  return countryLayers
}


function clampLatitude(latitude) {
  return Math.max(-WEB_MERCATOR_LAT_LIMIT, Math.min(WEB_MERCATOR_LAT_LIMIT, latitude))
}

function toMercator([longitude, latitude]) {
  const lat = clampLatitude(latitude) * Math.PI / 180
  return [
    (longitude + 180) / 360,
    (1 - Math.log(Math.tan(lat) + (1 / Math.cos(lat))) / Math.PI) / 2,
  ]
}

function fromMercator([x, y]) {
  const longitude = x * 360 - 180
  const n = Math.PI - 2 * Math.PI * y
  const latitude = 180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)))
  return [longitude, latitude]
}

function gentleCurve(from, to) {
  const start = toMercator(from)
  const end = toMercator(to)

  if (Math.abs(end[0] - start[0]) > 0.5) {
    end[0] += end[0] > start[0] ? -1 : 1
  }

  const dx = end[0] - start[0]
  const dy = end[1] - start[1]
  const length = Math.hypot(dx, dy)
  if (!Number.isFinite(length) || length === 0) return [from, to]

  const midpoint = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2]
  const perpendicular = [-dy / length, dx / length]
  const direction = perpendicular[1] <= 0 ? 1 : -1
  const offset = Math.min(length * 0.04, 0.004)
  const control = [
    midpoint[0] + perpendicular[0] * offset * direction,
    midpoint[1] + perpendicular[1] * offset * direction,
  ]

  const samples = Math.max(10, Math.min(34, Math.ceil(length * 900)))
  const points = []
  for (let index = 0; index <= samples; index += 1) {
    const t = index / samples
    const inverse = 1 - t
    let x = inverse * inverse * start[0] + 2 * inverse * t * control[0] + t * t * end[0]
    const y = inverse * inverse * start[1] + 2 * inverse * t * control[1] + t * t * end[1]
    x = ((x % 1) + 1) % 1
    points.push(fromMercator([x, y]))
  }
  return points
}

function pointInPolygon(longitude, latitude, polygon) {
  let inside = false
  for (let current = 0, previous = polygon.length - 1; current < polygon.length; previous = current, current += 1) {
    const [currentX, currentY] = polygon[current]
    const [previousX, previousY] = polygon[previous]
    const crosses = ((currentY > latitude) !== (previousY > latitude))
      && (longitude < ((previousX - currentX) * (latitude - currentY)) / (previousY - currentY) + currentX)
    if (crosses) inside = !inside
  }
  return inside
}

const GREAT_BRITAIN_OUTLINE = [
  [-5.85, 49.85], [-3.25, 49.85], [1.8, 50.65], [1.7, 52.15], [0.45, 53.75],
  [-1.25, 55.0], [-1.85, 57.65], [-3.15, 58.8], [-5.85, 58.7], [-6.55, 56.4],
  [-5.15, 54.65], [-4.85, 52.8], [-5.75, 51.35],
]

function isUkPoint(point) {
  const label = String(point?.label || '')
  if (/\b(uk|united kingdom|england|scotland|wales|northern ireland|leeds|lba|manchester|man|london|stansted|luton|gatwick|heathrow|malton|west knapton|east midlands|ema|birmingham|bristol|newcastle|edinburgh|glasgow|cardiff|belfast)\b/i.test(label)) {
    return true
  }

  const longitude = Number(point?.longitude)
  const latitude = Number(point?.latitude)
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return false

  const inGreatBritain = pointInPolygon(longitude, latitude, GREAT_BRITAIN_OUTLINE)
  const inNorthernIreland = longitude >= -8.25 && longitude <= -5.25 && latitude >= 54.0 && latitude <= 55.4
  return inGreatBritain || inNorthernIreland
}

function darkenHexColor(value, amount = 0.24) {
  const raw = String(value || '').trim().replace('#', '')
  const expanded = raw.length === 3 ? raw.split('').map((character) => character + character).join('') : raw
  if (!/^[0-9a-f]{6}$/i.test(expanded)) return '#fbbf8f'

  const channels = [0, 2, 4].map((index) => Number.parseInt(expanded.slice(index, index + 2), 16))
  const mixed = channels.map((channel) => Math.round(channel * (1 - amount)))
  return `#${mixed.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`
}

function validCoordinates(value) {
  if (!Array.isArray(value) || value.length < 2) return null
  const coordinates = value
    .map((point) => [Number(point?.[0]), Number(point?.[1])])
    .filter(([longitude, latitude]) => Number.isFinite(longitude) && Number.isFinite(latitude))
  return coordinates.length >= 2 ? coordinates : null
}

function buildRouteCollection(trips, routePoints, routeLegs, selectedTripId) {
  const selectedId = String(selectedTripId)
  const visibleTrips = selectedId === 'all'
    ? trips
    : trips.filter((trip) => String(trip.id) === selectedId)

  const exactLegs = new Map(
    (routeLegs || []).map((leg) => [`${leg.from_point_id}:${leg.to_point_id}`, leg]),
  )

  const features = visibleTrips.flatMap((trip) => {
    const tripId = String(trip.id)
    const points = routePoints
      .filter((point) => String(point.trip_id) === tripId)
      .sort((a, b) => Number(a.position) - Number(b.position))
      .filter((point) => Number.isFinite(Number(point.longitude)) && Number.isFinite(Number(point.latitude)))

    return points.slice(0, -1).flatMap((point, index) => {
      const nextPoint = points[index + 1]
      if (!nextPoint) return []
      const from = [Number(point.longitude), Number(point.latitude)]
      const to = [Number(nextPoint.longitude), Number(nextPoint.latitude)]
      const importedLeg = exactLegs.get(`${point.id}:${nextPoint.id}`)
      const importedCoordinates = validCoordinates(importedLeg?.coordinates)
      const manualArc = importedLeg?.source === 'manual'

      return [{
        type: 'Feature',
        properties: {
          id: `${trip.id}-${index}`,
          tripId: trip.id,
          name: trip.name,
          color: trip.color || '#f97316',
          ukLeg: isUkPoint(point) || isUkPoint(nextPoint) ? 1 : 0,
          exact: importedCoordinates && !manualArc ? 1 : 0,
          manualArc: manualArc ? 1 : 0,
          travelMode: importedLeg?.travel_mode || 'ARC',
        },
        geometry: {
          type: 'LineString',
          coordinates: importedCoordinates || gentleCurve(from, to),
        },
      }]
    })
  })

  return { type: 'FeatureCollection', features }
}

function buildStopCollection(trips, routePoints, selectedTripId) {
  const selectedId = String(selectedTripId)
  const visibleIds = new Set(
    (selectedId === 'all' ? trips : trips.filter((trip) => String(trip.id) === selectedId))
      .map((trip) => String(trip.id)),
  )

  return {
    type: 'FeatureCollection',
    features: routePoints
      .filter((point) => visibleIds.has(String(point.trip_id)))
      .filter((point) => point.kind !== 'summit')
      .filter((point) => Number.isFinite(Number(point.longitude)) && Number.isFinite(Number(point.latitude)))
      .map((point) => ({
        type: 'Feature',
        properties: {
          id: point.id,
          label: point.label,
          kind: point.kind,
          position: Number(point.position),
          tripId: point.trip_id,
        },
        geometry: {
          type: 'Point',
          coordinates: [Number(point.longitude), Number(point.latitude)],
        },
      })),
  }
}

function buildSummitCollection(summits, trips) {
  const tripColors = new Map(trips.map((trip) => [String(trip.id), trip.color || '#f97316']))

  return {
    type: 'FeatureCollection',
    features: summits
      .filter((summit) => Number.isFinite(Number(summit.longitude)) && Number.isFinite(Number(summit.latitude)))
      .map((summit) => {
        const pathColor = tripColors.get(String(summit.trip_id)) || '#94a3b8'
        return {
          type: 'Feature',
          properties: {
            id: summit.id,
            tripId: summit.trip_id || '',
            country: summit.country_name,
            peak: summit.peak_name,
            status: summit.status || 'future',
            elevation: Number(summit.elevation_m || 0),
            pathColor,
            markerColor: darkenHexColor(pathColor),
          },
          geometry: {
            type: 'Point',
            coordinates: [Number(summit.longitude), Number(summit.latitude)],
          },
        }
      }),
  }
}

function buildPhotoCollection(photos, selectedTripId) {
  return {
    type: 'FeatureCollection',
    features: photos
      .filter((photo) => selectedTripId === 'all' || String(photo.trip_id || '') === String(selectedTripId))
      .filter((photo) => Number.isFinite(Number(photo.longitude)) && Number.isFinite(Number(photo.latitude)))
      .map((photo) => ({
        type: 'Feature',
        properties: {
          id: photo.id,
          tripId: photo.trip_id || '',
          iconId: `photo-${photo.id}`,
          caption: photo.caption || '',
        },
        geometry: {
          type: 'Point',
          coordinates: [Number(photo.longitude), Number(photo.latitude)],
        },
      })),
  }
}


function formatSummitMonth(value) {
  if (!value) return ''
  const date = new Date(`${value}T12:00:00`)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('en-GB', { month: 'short', year: '2-digit' }).format(date)
}

function buildDateMarkerElement(text) {
  const element = document.createElement('span')
  element.className = 'summit-date-marker'
  element.textContent = text
  return element
}


// Build a plain DOM element for one photo thumbnail marker. HTML markers are
// positioned by MapLibre outside the WebGL worker, so a slow or missing R2
// thumbnail can never crash the style — the worst case is the camera fallback.
function buildPhotoMarkerElement(photo, onClick) {
  const element = document.createElement('button')
  element.type = 'button'
  element.className = 'photo-marker-html'
  element.setAttribute('aria-label', photo?.caption ? `Open photo: ${photo.caption}` : 'Open trip photo')

  const key = photo?.thumbnail_r2_key || photo?.r2_key
  const url = key ? photoAssetUrl(key) : ''
  if (url) {
    const image = document.createElement('img')
    image.src = url
    image.alt = ''
    image.loading = 'lazy'
    image.decoding = 'async'
    image.draggable = false
    image.addEventListener('error', () => {
      image.remove()
      element.classList.add('photo-marker-html--fallback')
    })
    element.appendChild(image)
  } else {
    element.classList.add('photo-marker-html--fallback')
  }

  element.addEventListener('click', (event) => {
    event.stopPropagation()
    onClick()
  })
  return element
}

// Photo pins stay deliberately small so they read as annotation on top of the
// journey rather than the subject of the map. They grow a little once you zoom
// into a specific area, where covering a country is no longer a risk.
function photoMarkerSize(zoom) {
  if (zoom <= 4) return 22
  if (zoom >= 9) return 38
  return Math.round(22 + ((zoom - 4) / 5) * 16)
}

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
}

function tourStopsFor(selectedTripId, routePoints, summits, trips) {
  if (String(selectedTripId) !== 'all') {
    const points = routePoints
      .filter((point) => String(point.trip_id) === String(selectedTripId))
      .filter((point) => Number.isFinite(Number(point.latitude)) && Number.isFinite(Number(point.longitude)))
      .sort((a, b) => Number(a.position) - Number(b.position))
    const tripName = trips.find((trip) => String(trip.id) === String(selectedTripId))?.name || 'Route'
    if (points.length) {
      return points.map((point) => ({
        id: `point-${point.id}`,
        label: point.label,
        sublabel: `${tripName} · stop ${point.position}`,
        latitude: Number(point.latitude),
        longitude: Number(point.longitude),
        zoom: point.kind === 'summit' ? TOUR_ZOOM.summit : TOUR_ZOOM.stop,
      }))
    }
  }

  const ordered = [...summits]
    .filter((summit) => Number.isFinite(Number(summit.latitude)) && Number.isFinite(Number(summit.longitude)))
    .sort((a, b) => {
      const aDate = a.summit_date || '9999-12-31'
      const bDate = b.summit_date || '9999-12-31'
      if (aDate !== bDate) return aDate < bDate ? -1 : 1
      return String(a.country_name || '').localeCompare(String(b.country_name || ''))
    })

  return ordered.map((summit) => ({
    id: `summit-${summit.id}`,
    label: summit.peak_name,
    sublabel: `${summit.country_name} · ${Number(summit.elevation_m || 0).toLocaleString()} m · ${summit.status || 'future'}`,
    latitude: Number(summit.latitude),
    longitude: Number(summit.longitude),
    zoom: TOUR_ZOOM.summit,
  }))
}

export default function SummitMap({
  trips,
  summits,
  routePoints,
  routeLegs,
  photos = [],
  selectedTripId,
  statusFilter,
  onSelectSummit,
  onSelectRoutePoint,
  onSelectPhoto,
  onMapApi,
  theme = 'dark',
}) {
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const loadedRef = useRef(false)
  const summitsRef = useRef(summits)
  const routePointsRef = useRef(routePoints)
  const photosRef = useRef(photos)
  const onSelectSummitRef = useRef(onSelectSummit)
  const onSelectRoutePointRef = useRef(onSelectRoutePoint)
  const onSelectPhotoRef = useRef(onSelectPhoto)
  const progressCountryLayersRef = useRef([])
  const routeCollectionRef = useRef(EMPTY_COLLECTION)
  const stopCollectionRef = useRef(EMPTY_COLLECTION)
  const summitCollectionRef = useRef(EMPTY_COLLECTION)
  const photoCollectionRef = useRef(EMPTY_COLLECTION)
  const visiblePhotosRef = useRef([])
  const photoMarkersRef = useRef(new Map())
  const showPhotosRef = useRef(true)
  const dateMarkersRef = useRef([])
  const dateLabelsRef = useRef([])
  const [showDates, setShowDates] = useState(true)
  const showUkLegsRef = useRef(false)
  const onMapApiRef = useRef(onMapApi)
  const tourTimerRef = useRef(null)
  const [showUkLegs, setShowUkLegs] = useState(false)
  const [showPhotos, setShowPhotos] = useState(true)
  const [tourActive, setTourActive] = useState(false)
  const [tourPlaying, setTourPlaying] = useState(false)
  const [tourIndex, setTourIndex] = useState(0)

  useEffect(() => { summitsRef.current = summits }, [summits])
  useEffect(() => { routePointsRef.current = routePoints }, [routePoints])
  useEffect(() => { photosRef.current = photos }, [photos])
  useEffect(() => { onSelectSummitRef.current = onSelectSummit }, [onSelectSummit])
  useEffect(() => { onSelectRoutePointRef.current = onSelectRoutePoint }, [onSelectRoutePoint])
  useEffect(() => { onSelectPhotoRef.current = onSelectPhoto }, [onSelectPhoto])
  useEffect(() => { onMapApiRef.current = onMapApi }, [onMapApi])

  const visibleSummits = useMemo(() => summits.filter((summit) => {
    const tripMatches = String(selectedTripId) === 'all' || String(summit.trip_id || '') === String(selectedTripId)
    const statusMatches = statusFilter === 'all' || summit.status === statusFilter
    return tripMatches && statusMatches
  }), [summits, selectedTripId, statusFilter])

  // One label per trip, not per peak. A trip with several summits gets a
  // single date at the midpoint of them, which also stops the Benelux
  // tripoint stacking three labels on top of each other. UK legs are excluded
  // from the midpoint so a Leeds departure doesn't drag the label into the sea.
  const dateLabels = useMemo(() => {
    const groups = new Map()

    visibleSummits
      .filter((summit) => summit.status !== 'completed')
      .forEach((summit) => {
        const longitude = Number(summit.longitude)
        const latitude = Number(summit.latitude)
        if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return

        const trip = trips.find((entry) => String(entry.id) === String(summit.trip_id))
        const date = summit.summit_date || trip?.start_date
        if (!formatSummitMonth(date)) return

        // Untripped summits stand alone rather than merging into one blob.
        const key = summit.trip_id ? `trip-${summit.trip_id}` : `summit-${summit.id}`
        if (!groups.has(key)) groups.set(key, { points: [], date, name: trip?.name || summit.country_name })
        const group = groups.get(key)
        group.points.push({ longitude, latitude, isUk: isUkPoint({ label: summit.country_name, longitude, latitude }) })
        if (String(date) < String(group.date)) group.date = date
      })

    return [...groups.entries()].map(([key, group]) => {
      const usable = group.points.filter((point) => !point.isUk)
      const points = usable.length ? usable : group.points
      const longitude = points.reduce((sum, point) => sum + point.longitude, 0) / points.length
      const latitude = points.reduce((sum, point) => sum + point.latitude, 0) / points.length
      return {
        id: key,
        label: formatSummitMonth(group.date),
        longitude,
        latitude,
        count: points.length,
      }
    })
  }, [visibleSummits, trips])

  const visiblePhotos = useMemo(
    () => photos.filter((photo) => selectedTripId === 'all' || String(photo.trip_id || '') === String(selectedTripId)),
    [photos, selectedTripId],
  )

  const photoCollection = useMemo(
    () => buildPhotoCollection(photos, selectedTripId),
    [photos, selectedTripId],
  )

  const routeCollection = useMemo(
    () => buildRouteCollection(trips, routePoints, routeLegs, selectedTripId),
    [trips, routePoints, routeLegs, selectedTripId],
  )

  const stopCollection = useMemo(
    () => buildStopCollection(trips, routePoints, selectedTripId),
    [trips, routePoints, selectedTripId],
  )

  const summitCollection = useMemo(
    () => buildSummitCollection(visibleSummits, trips),
    [visibleSummits, trips],
  )

  const themePalette = THEME_PALETTES[theme] || null

  // Bucketed to the day so the memo is stable within a session but still
  // drifts as trips get closer.
  const today = useMemo(() => new Date().setHours(12, 0, 0, 0), [])

  const progressCountryPaint = useMemo(
    () => (themePalette ? buildCountryProgressPaint(summits, themePalette, trips, today) : null),
    [summits, themePalette, trips, today],
  )

  const tourStops = useMemo(
    () => tourStopsFor(selectedTripId, routePoints, summits, trips),
    [selectedTripId, routePoints, summits, trips],
  )
  const tourStopsRef = useRef(tourStops)
  tourStopsRef.current = tourStops

  // Keep the latest cloud collections available to MapLibre's asynchronous
  // style-load callback. On a hosted build Supabase can refresh React state
  // before the map style finishes loading; using refs prevents stale/empty
  // journey data from being installed at that point.
  routeCollectionRef.current = routeCollection || EMPTY_COLLECTION
  stopCollectionRef.current = stopCollection || EMPTY_COLLECTION
  summitCollectionRef.current = summitCollection || EMPTY_COLLECTION
  photoCollectionRef.current = photoCollection || EMPTY_COLLECTION
  visiblePhotosRef.current = visiblePhotos
  showUkLegsRef.current = showUkLegs
  dateLabelsRef.current = dateLabels

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined

    const dark = theme !== 'light'
    const progress = Boolean(themePalette)
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: theme === 'dark'
        ? 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json'
        : 'https://demotiles.maplibre.org/style.json',
      center: [8, 43],
      zoom: 2.25,
      minZoom: 1.25,
      maxZoom: 13,
      attributionControl: true,
    })

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
    map.addControl(new maplibregl.FullscreenControl(), 'top-right')
    map.on('error', (event) => {
      // Keep the map usable if an optional image or basemap tile fails.
      console.error('Summit Hub map error:', event?.error || event)
    })

    map.on('load', () => {
      loadedRef.current = true
      progressCountryLayersRef.current = progress
        ? styleProgressBasemap(map, progressCountryPaint, themePalette)
        : []
      const labelColor = dark ? '#f8fafc' : '#111827'
      const haloColor = dark ? '#0b1020' : '#f8fafc'
      const stopFill = dark ? '#e2e8f0' : '#f8fafc'
      const stopStroke = dark ? '#020617' : '#111827'
      const shadowColor = dark ? '#020617' : '#111827'

      map.addSource('summit-routes', { type: 'geojson', data: routeCollectionRef.current })

      // Render every route feature through one resilient pair of layers.
      // The geometry can still be a Google path, a manual arc, or a generated
      // curve; filter-free styling prevents a missing feature property from
      // hiding the whole journey after a hosted deployment.
      map.addLayer({
        id: 'summit-routes-shadow',
        type: 'line',
        source: 'summit-routes',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': shadowColor,
          'line-width': 4.4,
          'line-opacity': [
            'case',
            ['==', ['get', 'ukLeg'], 1],
            showUkLegsRef.current ? (dark ? 0.12 : 0.08) : 0,
            dark ? 0.35 : 0.22,
          ],
          'line-blur': 0.4,
          'line-dasharray': [2.9, 1.8],
        },
      })
      map.addLayer({
        id: 'summit-routes-main',
        type: 'line',
        source: 'summit-routes',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': ['coalesce', ['get', 'color'], '#f97316'],
          'line-width': 2.6,
           'line-opacity': ['case', ['==', ['get', 'ukLeg'], 1], showUkLegsRef.current ? 0.24 : 0, 0.98],
          'line-dasharray': [2.9, 1.8],
        },
      })

      map.addSource('summit-stops', { type: 'geojson', data: stopCollectionRef.current })
      map.addLayer({
        id: 'summit-stops-layer',
        type: 'circle',
        source: 'summit-stops',
        paint: {
          'circle-radius': ['match', ['get', 'kind'], 'airport', 4.5, 'city', 4, 3.5],
          'circle-color': ['match', ['get', 'kind'], 'airport', stopFill, 'city', '#94a3b8', '#cbd5e1'],
          'circle-stroke-width': 1.7,
          'circle-stroke-color': stopStroke,
        },
      })
      map.addLayer({
        id: 'summit-stop-labels',
        type: 'symbol',
        source: 'summit-stops',
        minzoom: 5.25,
        layout: {
          'text-field': ['concat', ['to-string', ['get', 'position']], '. ', ['get', 'label']],
          'text-font': ['Open Sans Semibold'],
          'text-size': 11,
          'text-offset': [0, 1.15],
          'text-anchor': 'top',
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': labelColor,
          'text-halo-color': haloColor,
          'text-halo-width': 1.5,
        },
      })

      map.addSource('summit-points', { type: 'geojson', data: summitCollectionRef.current })
      map.addLayer({
        id: 'summit-points-halo',
        type: 'circle',
        source: 'summit-points',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 7, 8, 11],
          'circle-color': ['get', 'markerColor'],
          'circle-opacity': 0.22,
          'circle-blur': 0.5,
        },
      })
      map.addLayer({
        id: 'summit-points-hit',
        type: 'circle',
        source: 'summit-points',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 4.5, 8, 7],
          'circle-color': ['get', 'markerColor'],
          'circle-stroke-width': 2,
          'circle-stroke-color': stopStroke,
        },
      })
      map.addLayer({
        id: 'summit-point-labels',
        type: 'symbol',
        source: 'summit-points',
        minzoom: 4.25,
        layout: {
          'text-field': ['concat', ['get', 'peak'], '\n', ['get', 'country']],
          'text-font': ['Open Sans Semibold'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 4.25, 10, 8, 12],
          'text-offset': [0, 1.25],
          'text-anchor': 'top',
          'text-allow-overlap': false,
          'text-optional': true,
        },
        paint: {
          'text-color': labelColor,
          'text-halo-color': haloColor,
          'text-halo-width': 1.7,
        },
      })

      map.on('mouseenter', 'summit-points-hit', () => { map.getCanvas().style.cursor = 'pointer' })
      map.on('mouseleave', 'summit-points-hit', () => { map.getCanvas().style.cursor = '' })
      map.on('click', 'summit-points-hit', (event) => {
        const id = event.features?.[0]?.properties?.id
        const summit = summitsRef.current.find((item) => String(item.id) === String(id))
        if (summit) onSelectSummitRef.current(summit)
      })


      map.on('mouseenter', 'summit-stops-layer', () => { map.getCanvas().style.cursor = 'pointer' })
      map.on('mouseleave', 'summit-stops-layer', () => { map.getCanvas().style.cursor = '' })
      map.on('click', 'summit-stops-layer', (event) => {
        const id = event.features?.[0]?.properties?.id
        const point = routePointsRef.current.find((item) => String(item.id) === String(id))
        if (point) onSelectRoutePointRef.current?.(point)
      })

      map.addSource('summit-photos', {
        type: 'geojson',
        data: photoCollectionRef.current,
        cluster: true,
        clusterMaxZoom: 9,
        clusterRadius: 52,
      })
      // Clusters are intentionally restrained: a photo count should never be
      // large enough to hide the country underneath it.
      map.addLayer({
        id: 'summit-photo-clusters',
        type: 'circle',
        source: 'summit-photos',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': dark ? '#17211c' : '#ffffff',
          'circle-radius': ['step', ['get', 'point_count'], 11, 10, 13, 50, 15],
          'circle-stroke-width': 1.6,
          'circle-stroke-color': '#d8ff63',
          'circle-opacity': 0.82,
        },
      })
      map.addLayer({
        id: 'summit-photo-cluster-count',
        type: 'symbol',
        source: 'summit-photos',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font': ['Open Sans Semibold'],
          'text-size': 9,
        },
        paint: { 'text-color': dark ? '#f5f2e8' : '#17211c' },
      })
      // Photo pins are HTML markers rather than a MapLibre image or circle
      // layer. The clustered GeoJSON source above still decides *which* photos
      // are unclustered at the current zoom; this sync mirrors that answer into
      // DOM markers. HTML markers live outside the WebGL worker, so thumbnail
      // decoding can never trigger the production worker crash that dynamic
      // icon-image expressions used to ("Js is not defined"), and the source is
      // still created once with its final collection — no setData() diffing.
      const applyMarkerScale = () => {
        containerRef.current?.style.setProperty('--photo-marker-size', `${photoMarkerSize(map.getZoom())}px`)
      }
      applyMarkerScale()
      map.on('zoom', applyMarkerScale)

      const syncPhotoMarkers = () => {
        const markers = photoMarkersRef.current
        if (!showPhotosRef.current) {
          markers.forEach((marker) => marker.remove())
          markers.clear()
          return
        }
        if (!map.getSource('summit-photos') || !map.isSourceLoaded('summit-photos')) return

        const unclustered = new Set()
        map.querySourceFeatures('summit-photos').forEach((feature) => {
          if (feature.properties?.cluster) return
          const id = String(feature.properties?.id ?? '')
          if (!id || unclustered.has(id)) return
          unclustered.add(id)

          if (!markers.has(id)) {
            const photo = photosRef.current.find((item) => String(item.id) === id)
            if (!photo) return
            const element = buildPhotoMarkerElement(photo, () => onSelectPhotoRef.current?.(photo))
            const marker = new maplibregl.Marker({ element, anchor: 'center' })
              .setLngLat(feature.geometry.coordinates)
              .addTo(map)
            markers.set(id, marker)
          }
        })

        markers.forEach((marker, id) => {
          if (!unclustered.has(id)) {
            marker.remove()
            markers.delete(id)
          }
        })
      }
      map.on('render', syncPhotoMarkers)
      syncPhotoMarkers()

      // Planned-date labels. HTML markers again, so a missing date or an odd
      // string can never reach the style worker. Visibility is handled purely
      // in CSS via .map-canvas--no-dates, which keeps the toggle independent of
      // the map lifecycle.
      //
      // Position comes from the basemap's own country label. The demotiles
      // style draws country names from a "centroids" source layer carrying the
      // same ADM0_A3 code the progress fills use, so the date sits exactly
      // where the map has decided the country's name belongs — better than any
      // generic centroid, which puts Spain in the Canaries. The peak's own
      // coordinates are the fallback until (or unless) that lookup succeeds.
      dateMarkersRef.current.forEach(({ marker }) => marker.remove())
      dateMarkersRef.current = []
      dateLabelsRef.current.forEach((entry) => {
        const marker = new maplibregl.Marker({
          element: buildDateMarkerElement(entry.label),
          anchor: entry.count > 1 ? 'center' : 'top',
          offset: entry.count > 1 ? [0, 0] : [0, 13],
        })
          .setLngLat([entry.longitude, entry.latitude])
          .addTo(map)
        dateMarkersRef.current.push({ marker, entry })
      })

      map.on('mouseenter', 'summit-photo-clusters', () => { map.getCanvas().style.cursor = 'pointer' })
      map.on('mouseleave', 'summit-photo-clusters', () => { map.getCanvas().style.cursor = '' })
      map.on('click', 'summit-photo-clusters', async (event) => {
        const feature = event.features?.[0]
        const clusterId = feature?.properties?.cluster_id
        const source = map.getSource('summit-photos')
        if (clusterId === undefined || !source) return
        try {
          const zoom = await source.getClusterExpansionZoom(clusterId)
          map.easeTo({ center: feature.geometry.coordinates, zoom, duration: 500 })
        } catch (_error) {
          map.easeTo({ center: feature.geometry.coordinates, zoom: Math.min(map.getZoom() + 2, 11), duration: 500 })
        }
      })

      // Each GeoJSON source is created once with its final collection. The map
      // itself is recreated when those memoised collections change, avoiding
      // MapLibre's production-only GeoJSON diff worker failure.
    })

    mapRef.current = map
    const observer = new ResizeObserver(() => map.resize())
    observer.observe(containerRef.current)

    return () => {
      observer.disconnect()
      photoMarkersRef.current.forEach((marker) => marker.remove())
      photoMarkersRef.current.clear()
      dateMarkersRef.current.forEach(({ marker }) => marker.remove())
      dateMarkersRef.current = []
      map.remove()
      mapRef.current = null
      loadedRef.current = false
    }
  }, [theme, themePalette, routeCollection, stopCollection, summitCollection, photoCollection, progressCountryPaint])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !loadedRef.current || !progressCountryPaint) return
    progressCountryLayersRef.current.forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.setPaintProperty(layerId, 'fill-color', progressCountryPaint)
      }
    })
  }, [progressCountryPaint, theme])

  useEffect(() => {
    containerRef.current?.classList.toggle('map-canvas--no-dates', !showDates)
  }, [showDates])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !loadedRef.current) return
    if (map.getLayer('summit-routes-main')) {
      map.setPaintProperty('summit-routes-main', 'line-opacity', [
        'case',
        ['==', ['get', 'ukLeg'], 1],
        showUkLegs ? 0.24 : 0,
        0.98,
      ])
    }
    if (map.getLayer('summit-routes-shadow')) {
      map.setPaintProperty('summit-routes-shadow', 'line-opacity', [
        'case',
        ['==', ['get', 'ukLeg'], 1],
        showUkLegs ? (theme === 'light' ? 0.08 : 0.12) : 0,
        theme === 'light' ? 0.22 : 0.35,
      ])
    }
  }, [showUkLegs, theme])


  useEffect(() => {
    showPhotosRef.current = showPhotos
    const map = mapRef.current
    if (!map || !loadedRef.current) return
    ;['summit-photo-clusters', 'summit-photo-cluster-count'].forEach((layerId) => {
      if (map.getLayer(layerId)) map.setLayoutProperty(layerId, 'visibility', showPhotos ? 'visible' : 'none')
    })
    if (!showPhotos) {
      photoMarkersRef.current.forEach((marker) => marker.remove())
      photoMarkersRef.current.clear()
    } else {
      // The render-synced marker pass repopulates thumbnails on the next frame.
      map.triggerRepaint()
    }
  }, [showPhotos])

  const stopTour = useCallback(() => {
    window.clearTimeout(tourTimerRef.current)
    setTourPlaying(false)
    setTourActive(false)
    setTourIndex(0)
  }, [])

  const startTour = useCallback(() => {
    setTourIndex(0)
    setTourActive(true)
    setTourPlaying(true)
  }, [])

  // Expose a small imperative surface so the command palette can drive the map
  // without App reaching into MapLibre itself.
  useEffect(() => {
    const api = {
      flyTo: (longitude, latitude, zoom = 7.4) => {
        const map = mapRef.current
        if (!map || !Number.isFinite(Number(longitude)) || !Number.isFinite(Number(latitude))) return
        map.flyTo({
          center: [Number(longitude), Number(latitude)],
          zoom,
          duration: prefersReducedMotion() ? 0 : 1500,
          essential: true,
        })
      },
      startTour,
      stopTour,
      hasTour: () => tourStopsRef.current.length > 1,
    }
    onMapApiRef.current?.(api)
    return () => onMapApiRef.current?.(null)
  }, [startTour, stopTour])

  // Fly the map to the current tour stop, then queue the next one.
  useEffect(() => {
    window.clearTimeout(tourTimerRef.current)
    const map = mapRef.current
    const stop = tourStops[tourIndex]
    if (!tourActive || !map || !stop) return undefined

    const reduced = prefersReducedMotion()
    map.flyTo({
      center: [stop.longitude, stop.latitude],
      zoom: stop.zoom,
      duration: reduced ? 0 : 2800,
      essential: true,
    })

    if (!tourPlaying) return undefined
    if (tourIndex >= tourStops.length - 1) {
      tourTimerRef.current = window.setTimeout(() => setTourPlaying(false), reduced ? 600 : 2600)
      return () => window.clearTimeout(tourTimerRef.current)
    }
    tourTimerRef.current = window.setTimeout(
      () => setTourIndex((current) => Math.min(current + 1, tourStops.length - 1)),
      reduced ? 900 : 3600,
    )
    return () => window.clearTimeout(tourTimerRef.current)
  }, [tourActive, tourPlaying, tourIndex, tourStops])

  // Changing the trip filter changes the itinerary, so reset any running tour.
  useEffect(() => {
    setTourActive(false)
    setTourPlaying(false)
    setTourIndex(0)
  }, [selectedTripId])

  useEffect(() => () => window.clearTimeout(tourTimerRef.current), [])

  useEffect(() => {
    const map = mapRef.current
    if (!map || selectedTripId === 'all') return

    const tripRoutePoints = routePoints.filter((point) => String(point.trip_id) === String(selectedTripId))
    const nonUkRoutePoints = tripRoutePoints.filter((point) => !isUkPoint(point))
    const tripSummits = summits.filter((summit) => String(summit.trip_id || '') === String(selectedTripId))
    const points = [...nonUkRoutePoints, ...tripSummits]
    const fallbackPoints = points.length ? points : [...tripRoutePoints, ...tripSummits]
    if (!fallbackPoints.length) return

    const bounds = new maplibregl.LngLatBounds()
    fallbackPoints.forEach((point) => {
      const longitude = Number(point.longitude)
      const latitude = Number(point.latitude)
      if (Number.isFinite(longitude) && Number.isFinite(latitude)) bounds.extend([longitude, latitude])
    })

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, {
        padding: { top: 110, right: 90, bottom: 110, left: 90 },
        maxZoom: 8,
        duration: 750,
      })
    }
  }, [selectedTripId, routePoints, summits])

  const currentStop = tourStops[tourIndex] || null

  return (
    <>
      <div ref={containerRef} className="map-canvas" aria-label="Interactive summit map" />

      {tourActive && currentStop && (
        <div className="tour-bar glass-panel" role="region" aria-label="Expedition tour">
          <div className="tour-bar__copy">
            <div className="eyebrow">Flying the route · {tourIndex + 1} of {tourStops.length}</div>
            <strong>{currentStop.label}</strong>
            <small>{currentStop.sublabel}</small>
          </div>
          <div className="tour-bar__controls">
            <button
              type="button"
              onClick={() => { setTourPlaying(false); setTourIndex((current) => Math.max(0, current - 1)) }}
              disabled={tourIndex === 0}
              aria-label="Previous stop"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              className="tour-bar__play"
              onClick={() => setTourPlaying((current) => !current)}
              aria-label={tourPlaying ? 'Pause tour' : 'Play tour'}
            >
              {tourPlaying ? <Pause size={16} /> : <Play size={16} />}
            </button>
            <button
              type="button"
              onClick={() => { setTourPlaying(false); setTourIndex((current) => Math.min(tourStops.length - 1, current + 1)) }}
              disabled={tourIndex >= tourStops.length - 1}
              aria-label="Next stop"
            >
              <ChevronRight size={16} />
            </button>
            <button type="button" onClick={stopTour} aria-label="Exit tour"><Square size={14} /></button>
          </div>
          <div className="tour-bar__track" aria-hidden="true">
            <i style={{ width: `${((tourIndex + 1) / tourStops.length) * 100}%` }} />
          </div>
        </div>
      )}

      <div className="map-layer-controls glass-panel">
        <label className="map-toggle">
          <input type="checkbox" checked={showUkLegs} onChange={(event) => setShowUkLegs(event.target.checked)} />
          <span>UK legs</span>
        </label>
        <small>Faded</small>
        <label className="map-toggle">
          <input type="checkbox" checked={showPhotos} onChange={(event) => setShowPhotos(event.target.checked)} />
          <span>Photos</span>
        </label>
        <small>{visiblePhotos.length}</small>
        <label className="map-toggle">
          <input type="checkbox" checked={showDates} onChange={(event) => setShowDates(event.target.checked)} />
          <span>Dates</span>
        </label>
        <small>{dateLabels.length}</small>
        {tourStops.length > 1 && !tourActive && (
          <button type="button" className="tour-launch" onClick={startTour}>
            <Play size={13} /> Fly route
          </button>
        )}
        {Boolean(themePalette) && (
          <div className="progress-map-legend" aria-label="Country progress colours">
            <span><i className="progress-map-legend__done" />Completed</span>
            <span><i className="progress-map-legend__planned" />Planned</span>
          </div>
        )}
      </div>
    </>
  )
}
